import {
  ChangeEvent,
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { InputMask } from "../InputMask";
import { TextField, TextFieldProps } from "../TextField";
import { PopoverLegacy } from "../PopoverLegacy";
import {
  CalendarProps,
  CalendarValue,
  CalendarRangeValue,
} from "../Calendar/Calendar";
import { Calendar } from "../Calendar";
import { useMergeRefs } from "@floating-ui/react";
import { IanaZone, Locale } from "../../types";
import { Icon } from "../Icon";
import Event from "@servicetitan/hammer-icon/mdi/round/event.svg";
import { DateTime } from "luxon";
import { isValidDate, toDateString } from "../Calendar/utils";

const RANGE_DIVIDER = " — ";

interface DateFieldLegacyCommonProps {
  format?: "MM/dd/yyyy" | "dd/MM/yyyy";
  locale?: Locale;
  defaultTimeZone?: IanaZone;
  openCalendar?: boolean;
  disableHint?: boolean;
}

export interface DateSelect {
  value: string;
  date: string | null | undefined;
  isValid: boolean;
}

export interface DateRangeSelect {
  start?: DateSelect;
  end?: DateSelect;
  value: string;
}

interface DateFieldLegacySingleProps {
  onChange?: (event: ChangeEvent<HTMLInputElement>, data: DateSelect) => void;
  defaultValue?: string;
  range?: false;
}

interface DateFieldLegacyRangeProps {
  onChange?: (
    event: ChangeEvent<HTMLInputElement>,
    data: DateRangeSelect,
  ) => void;
  defaultValue?: { start?: string; end?: string };
  range: true;
}

export type DateFieldLegacyProps = Omit<CalendarProps, "onChange"> &
  Omit<
    TextFieldProps,
    "prefix" | "suffix" | "hint" | "onChange" | "showCounter"
  > &
  DateFieldLegacyCommonProps &
  (DateFieldLegacySingleProps | DateFieldLegacyRangeProps);

export const DateFieldLegacy = forwardRef<
  HTMLInputElement,
  DateFieldLegacyProps
>(
  (
    {
      // DateFieldLegacy
      openCalendar,
      onFocus,
      onBlur,
      onChange,
      format = "MM/dd/yyyy",
      disableHint,

      // Calendar
      minDate,
      maxDate,
      unavailableDates,
      unavailable,
      startDay,
      defaultValue,
      value: inputValue,

      // Shared
      locale = Intl.DateTimeFormat().resolvedOptions().locale as Locale,
      defaultTimeZone = Intl.DateTimeFormat().resolvedOptions()
        .timeZone as IanaZone,
      range,

      // Input
      required,
      ...inputProps
    },
    ref,
  ) => {
    const [open, setOpen] = useState(openCalendar || false);
    const [internalValue, setInternalValue] = useState<
      DateSelect | DateRangeSelect | undefined
    >();
    const [disableFocusLock, setDisableFocusLock] = useState(true);
    const inputRef = useRef<HTMLInputElement>(null);
    const calRef = useRef<HTMLDivElement>(null);
    const iconRef = useRef<HTMLElement>(null);
    const docRef = useRef<Document | Node | null>(null);
    const floatRef = useRef<HTMLDivElement>(null);
    const isClickOutside = useRef(false);
    const formatMask = format.replace(/[^/]/g, "_");
    const hint = useMemo(
      () => (disableHint ? undefined : `Format: ${format.replace(/M/g, "m")}`),
      [format, disableHint],
    );

    useEffect(() => {
      if (!docRef.current) {
        if (document != null) {
          docRef.current = document;
          return;
        }
        const doc = inputRef.current?.closest("body")?.ownerDocument;
        if (doc) {
          docRef.current = doc;
        }

        if (docRef.current == null && inputRef.current?.parentNode != null) {
          let node: Node = inputRef.current?.parentNode;
          while (node.parentNode != null) {
            node = node.parentNode;
          }
          docRef.current = node;
        }
      }
      const clickOutHandler = (ev: Event) => {
        // this is more accurate, especially in shadow doms
        const realTarget = ev.composedPath()[0] as Node;

        if (
          !floatRef.current?.contains(realTarget) &&
          !iconRef.current?.contains(realTarget) &&
          inputRef.current !== realTarget
        ) {
          setOpen(false);
          setDisableFocusLock(true);
          onBlur?.(ev as unknown as React.FocusEvent<HTMLInputElement>);
        }
      };

      if (open) {
        docRef.current?.addEventListener("click", clickOutHandler);
      } else {
        docRef.current?.removeEventListener("click", clickOutHandler);
      }

      return () => {
        docRef.current?.removeEventListener("click", clickOutHandler);
      };
    }, [open, onBlur]);

    useEffect(() => {
      if (range) {
        let start;
        let end;
        if (typeof inputValue === "string") {
          const parts = inputValue.split(RANGE_DIVIDER);
          start = parts[0];
          end = parts[1];
        } else {
          start = inputValue?.start ?? "";
          end = inputValue?.end ?? "";
        }
        const startDate = DateTime.fromFormat(start ?? "", format, {
          zone: defaultTimeZone,
        }).startOf("day");
        const endDate = DateTime.fromFormat(end ?? "", format, {
          zone: defaultTimeZone,
        }).startOf("day");
        const newData: DateRangeSelect = {
          start: {
            value: start,
            date: toDateString(startDate),
            isValid:
              startDate.isValid &&
              isValidDate(
                startDate,
                minDate,
                maxDate,
                new Set(unavailableDates || unavailable?.dates),
                {
                  dates: new Set(unavailable?.dates),
                  daysOfWeek: new Set(unavailable?.daysOfWeek),
                },
                defaultTimeZone,
              ),
          },
          end: {
            value: end,
            date: toDateString(endDate),
            isValid:
              startDate.isValid &&
              isValidDate(
                endDate,
                minDate,
                maxDate,
                new Set(unavailableDates || unavailable?.dates),
                {
                  dates: new Set(unavailable?.dates),
                  daysOfWeek: new Set(unavailable?.daysOfWeek),
                },
                defaultTimeZone,
              ),
          },
          value: `${start || formatMask}${RANGE_DIVIDER}${end || formatMask}`,
        };
        setInternalValue(newData);
      } else {
        if (typeof inputValue !== "string") return;
        const inputDate = DateTime.fromFormat(inputValue, format, {
          zone: defaultTimeZone,
        }).startOf("day");
        const newData: DateSelect = {
          value: inputValue,
          date: toDateString(inputDate),
          isValid:
            inputDate.isValid &&
            isValidDate(
              inputDate,
              minDate,
              maxDate,
              new Set(unavailableDates || unavailable?.dates),
              {
                dates: new Set(unavailable?.dates),
                daysOfWeek: new Set(unavailable?.daysOfWeek),
              },
              defaultTimeZone,
            ),
        };
        setInternalValue(newData);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue]);

    const mask: string = useMemo(() => {
      const maskPart = format.replace(/[Mdy]/g, "9");
      if (range) {
        return maskPart + RANGE_DIVIDER + maskPart;
      } else {
        return maskPart;
      }
    }, [format, range]);

    useEffect(() => {
      // Set default only if uncontrolled and we have a defaultValue.
      if (typeof inputValue === "string") return;
      if (defaultValue) {
        if (range) {
          // Get input value
          const defValStart = defaultValue.start;
          const defValEnd = defaultValue.end;

          const initValStart =
            defValStart +
            (formatMask + RANGE_DIVIDER + formatMask).slice(
              defValStart?.length,
            );
          const initValEnd =
            defValEnd +
            (formatMask + RANGE_DIVIDER + formatMask).slice(defValEnd?.length);
          const newValue = initValStart + RANGE_DIVIDER + initValEnd;

          // Set calendar value
          const inputDateStart = DateTime.fromFormat(
            defValStart || "",
            format,
            {
              zone: defaultTimeZone,
            },
          ).startOf("day");
          const inputDateEnd = DateTime.fromFormat(defValEnd || "", format, {
            zone: defaultTimeZone,
          }).startOf("day");

          const isValidStart =
            inputDateStart.isValid &&
            isValidDate(
              inputDateStart,
              minDate,
              maxDate,
              new Set(unavailableDates || unavailable?.dates),
              {
                dates: new Set(unavailable?.dates),
                daysOfWeek: new Set(unavailable?.daysOfWeek),
              },
              defaultTimeZone,
            );

          const isValidEnd =
            inputDateEnd.isValid &&
            isValidDate(
              inputDateEnd,
              minDate,
              maxDate,
              new Set(unavailableDates || unavailable?.dates),
              {
                dates: new Set(unavailable?.dates),
                daysOfWeek: new Set(unavailable?.daysOfWeek),
              },
              defaultTimeZone,
            );
          const initCalendarValue: DateRangeSelect = {
            start: {
              value: initValStart,
              date: toDateString(inputDateStart),
              isValid: isValidStart,
            },
            end: {
              value: initValEnd,
              date: toDateString(inputDateEnd),
              isValid: isValidEnd,
            },
            value: newValue,
          };
          setInternalValue(initCalendarValue);
        } else {
          // Get input value
          const defVal = defaultValue as string;
          const initVal = defVal + formatMask.slice(defVal.length);

          // Set calendar value
          const inputDate = DateTime.fromFormat(defVal, format, {
            zone: defaultTimeZone,
          }).startOf("day");
          const isValid =
            inputDate.isValid &&
            isValidDate(
              inputDate,
              minDate,
              maxDate,
              new Set(unavailableDates || unavailable?.dates),
              {
                dates: new Set(unavailable?.dates),
                daysOfWeek: new Set(unavailable?.daysOfWeek),
              },
              defaultTimeZone,
            );
          const initCalendarValue: DateSelect = {
            value: initVal,
            date: toDateString(inputDate),
            isValid,
          };
          setInternalValue(initCalendarValue);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const focusToCalendar = () => {
      if (calRef.current) {
        const currentFocusable =
          calRef.current.querySelectorAll('[tabindex = "0"]')[0];
        if (currentFocusable) {
          (currentFocusable as HTMLButtonElement).focus();
        }
      }
    };

    const handleInputKeyDown: KeyboardEventHandler<HTMLInputElement> = (ev) => {
      switch (ev.key) {
        case "Tab":
          setOpen(false);
          break;
        case "ArrowDown":
          if (!open) {
            setOpen(true);
            setTimeout(focusToCalendar, 100);
          } else {
            focusToCalendar();
          }
      }
    };

    const handleInputChange: ChangeEventHandler<HTMLInputElement> = (ev) => {
      if (range) {
        const parts = ev.target.value.split(RANGE_DIVIDER);
        if (parts.length !== 2) return;
        const startDate = DateTime.fromFormat(parts[0], format, {
          zone: defaultTimeZone,
        }).startOf("day");
        const endDate = DateTime.fromFormat(parts[1], format, {
          zone: defaultTimeZone,
        }).startOf("day");
        const returnValue: DateRangeSelect = {
          start: {
            value: parts[0],
            date: toDateString(startDate),
            isValid:
              startDate.isValid &&
              isValidDate(
                startDate,
                minDate,
                maxDate,
                new Set(unavailableDates || unavailable?.dates),
                {
                  dates: new Set(unavailable?.dates),
                  daysOfWeek: new Set(unavailable?.daysOfWeek),
                },
                defaultTimeZone,
              ),
          },
          end: {
            value: parts[1],
            date: toDateString(endDate),
            isValid:
              startDate.isValid &&
              isValidDate(
                endDate,
                minDate,
                maxDate,
                new Set(unavailableDates || unavailable?.dates),
                {
                  dates: new Set(unavailable?.dates),
                  daysOfWeek: new Set(unavailable?.daysOfWeek),
                },
                defaultTimeZone,
              ),
          },
          value: ev.target.value,
        };
        if (!inputValue) {
          setInternalValue(returnValue);
        }
        onChange?.(ev, returnValue);
      } else {
        if (ev.target.value === (internalValue as DateSelect)?.date) return;
        // Check if the value is a real date.
        const inputDate = DateTime.fromFormat(ev.target.value, format, {
          zone: defaultTimeZone,
        }).startOf("day");
        const returnValue: DateSelect = {
          value: ev.target.value,
          date: toDateString(inputDate),
          isValid:
            inputDate.isValid &&
            isValidDate(
              inputDate,
              minDate,
              maxDate,
              new Set(unavailableDates || unavailable?.dates),
              {
                dates: new Set(unavailable?.dates),
                daysOfWeek: new Set(unavailable?.daysOfWeek),
              },
              defaultTimeZone,
            ),
        };
        if (!inputValue) {
          setInternalValue(returnValue);
        }
        onChange?.(ev, returnValue);
      }
    };

    const handleInputBlur: React.FocusEventHandler<HTMLInputElement> = (ev) => {
      if (isClickOutside.current) {
        isClickOutside.current = false;
        return;
      }

      if (!calRef.current?.contains(ev.relatedTarget as Node)) {
        setOpen(false);
        onBlur?.(ev);
      }
    };

    const handleCalendarKeyDown: KeyboardEventHandler<HTMLDivElement> = (
      ev,
    ) => {
      switch (ev.key) {
        case "Escape":
          setDisableFocusLock(true);
          inputRef.current?.focus();
          break;
      }
    };

    const handleCalendarBlur: React.FocusEventHandler<HTMLDivElement> = (
      ev,
    ) => {
      if (
        !(
          inputRef.current === ev.currentTarget ||
          calRef.current === ev.currentTarget
        )
      ) {
        setOpen(false);
        onBlur?.(ev);
      }
      if (!(calRef.current === ev.currentTarget)) {
        setDisableFocusLock(true);
      }
    };

    const handleCalendarFocus: React.FocusEventHandler = (_event) => {
      if (disableFocusLock) {
        setDisableFocusLock(false);
      }
    };

    const handleCalendarSelection = (
      data: { value: CalendarValue } | { value: CalendarRangeValue },
    ) => {
      if (range) {
        const calSelectData = data as { value: CalendarRangeValue };
        const startVal = DateTime.fromISO(calSelectData?.value?.start || "", {
          zone: defaultTimeZone,
        });
        const endVal = DateTime.fromISO(calSelectData?.value?.end || "", {
          zone: defaultTimeZone,
        });
        let newValue = "";
        const newInternalValue: DateRangeSelect = { value: "" };
        if (startVal.isValid) {
          newInternalValue.start = {
            value: startVal.toFormat(format),
            date: calSelectData?.value?.start,
            isValid: true,
          };
          newValue += startVal.toFormat(format);
        } else {
          newValue += formatMask;
        }
        newValue += RANGE_DIVIDER;
        if (endVal.isValid) {
          newInternalValue.end = {
            value: endVal.toFormat(format),
            date: calSelectData?.value?.end,
            isValid: true,
          };
          newValue += endVal.toFormat(format);
        } else {
          newValue += formatMask;
        }
        if (typeof inputValue !== "string") {
          setInternalValue(newInternalValue);
        }
        newInternalValue.value = newValue;
        const event = {
          target: {
            value: newValue,
          },
        } as ChangeEvent<HTMLInputElement>;
        onChange?.(event, newInternalValue);
      } else {
        const calSelectData = data as { value: CalendarValue };
        const val = DateTime.fromISO(calSelectData.value || "", {
          zone: defaultTimeZone,
        });
        const newValue = val.toFormat(format);
        const newInternalValue: DateSelect = {
          value: newValue,
          date: calSelectData.value,
          isValid:
            val.isValid &&
            isValidDate(
              val,
              minDate,
              maxDate,
              new Set(unavailableDates || unavailable?.dates),
              {
                dates: new Set(unavailable?.dates),
                daysOfWeek: new Set(unavailable?.daysOfWeek),
              },
              defaultTimeZone,
            ),
        };
        if (typeof inputValue !== "string") {
          setInternalValue(newInternalValue);
        }
        const event = {
          target: {
            value: newValue,
          },
        } as ChangeEvent<HTMLInputElement>;
        onChange?.(event, newInternalValue);
      }
    };

    const handleInputFocus: FocusEventHandler<HTMLInputElement> = (ev) => {
      if (!open) {
        setOpen(true);
        onFocus?.(ev);
      }
    };

    return (
      <PopoverLegacy
        open={openCalendar || open}
        modal
        placement="bottom-start"
        disableFlipFallback
        disableTriggerFocus
        disableFocusLock={disableFocusLock}
      >
        <PopoverLegacy.Trigger>
          {({ ref: popoverRef, ...props }) => (
            <>
              <InputMask
                mask={mask}
                inputProps={{
                  onKeyDown: handleInputKeyDown,
                  onBlur: handleInputBlur,
                  onChange: handleInputChange,
                  onFocus: handleInputFocus,
                }}
                value={internalValue?.value || ""}
                {...props}
                // eslint-disable-next-line react-hooks/rules-of-hooks
                ref={useMergeRefs([popoverRef, inputRef])}
              >
                <TextField
                  hint={hint}
                  ref={ref}
                  prefix={<Icon svg={Event} ref={iconRef} />}
                  {...inputProps}
                  required={required}
                />
              </InputMask>
            </>
          )}
        </PopoverLegacy.Trigger>
        <PopoverLegacy.Content ref={floatRef}>
          {!range ? (
            <Calendar
              ref={calRef}
              onKeyDown={handleCalendarKeyDown}
              defaultFocusedDate={(internalValue as DateSelect)?.date}
              value={(internalValue as DateSelect)?.date || undefined}
              locale={locale}
              onBlur={handleCalendarBlur}
              onSelection={handleCalendarSelection}
              onFocus={handleCalendarFocus}
              minDate={minDate}
              maxDate={maxDate}
              unavailableDates={unavailableDates}
              unavailable={unavailable}
              defaultTimeZone={defaultTimeZone}
              startDay={startDay}
            />
          ) : (
            <Calendar
              ref={calRef}
              onKeyDown={handleCalendarKeyDown}
              defaultFocusedDate={
                (internalValue as DateRangeSelect)?.start?.date
              }
              value={
                internalValue
                  ? {
                      start:
                        (internalValue as DateRangeSelect)?.start?.date ||
                        undefined,
                      end:
                        (internalValue as DateRangeSelect)?.end?.date ||
                        undefined,
                    }
                  : undefined
              }
              locale={locale}
              onBlur={handleCalendarBlur}
              onSelection={handleCalendarSelection}
              onFocus={handleCalendarFocus}
              minDate={minDate}
              maxDate={maxDate}
              unavailableDates={unavailableDates}
              unavailable={unavailable}
              defaultTimeZone={defaultTimeZone}
              startDay={startDay}
              range
            />
          )}
        </PopoverLegacy.Content>
      </PopoverLegacy>
    );
  },
);

DateFieldLegacy.displayName = "DateFieldLegacy";
